import { storage } from "@/Firebase";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import L from "leaflet";
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";
import "leaflet/dist/leaflet.css";
import { useCallback, useEffect, useState } from "react";
import {
  MapContainer, Marker, Popup,
  TileLayer, useMap, useMapEvents,
} from "react-leaflet";
import ImageUploader from "./ImageUploader";
import styles from "./ImmoForm.module.css";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

// ─── Helpers carte ───────────────────────────────────────────────────────────
function FlyToPosition({ position }) {
  const map = useMap();
  useEffect(() => {
    if (position) map.flyTo(position, 14, { animate: true });
  }, [position, map]);
  return null;
}

function ClickHandler({ onMapClick }) {
  useMapEvents({
    click(e) { onMapClick(e.latlng.lat, e.latlng.lng); },
  });
  return null;
}

// ─── Form initial ────────────────────────────────────────────────────────────
const initialForm = {
  name: "",
  price: "",
  priceType: "total",
  location: "",
  latitude: null,
  longitude: null,
  area: "",
  rooms: "",
  description: "",
  images: [],
  imagesUrls: [],
  mainImageUrl: "",
  livreFoncier: null,
  acte: null,
  vsp: null,
};

// ─── Composant principal ─────────────────────────────────────────────────────
const ImmoForm = ({
  isEditing,
  formData,
  onAdd,
  onUpdate,
  processing,
  addTitle = "➕ Ajouter une nouvelle propriété",
  editTitle = "✏️ Modifier la propriété",
})  => {
  const [localForm, setLocalForm] = useState(initialForm);
  const [uploading, setUploading] = useState(false);
  const [markerPosition, setMarkerPosition] = useState(null);
  const [mapReady, setMapReady] = useState(false);

  useEffect(() => {
    if (formData) {
      setLocalForm({ ...initialForm, ...formData, images: [] });
      if (formData.latitude && formData.longitude) {
        setMarkerPosition([formData.latitude, formData.longitude]);
      }
    }
  }, [formData]);

  useEffect(() => {
    const t = setTimeout(() => setMapReady(true), 100);
    return () => clearTimeout(t);
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setLocalForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const filesArray = Array.from(e.target.files);
    setLocalForm((prev) => ({
      ...prev,
      images: [...(prev.images || []), ...filesArray],
    }));
  };

  const handleMapClick = useCallback((lat, lng) => {
    setMarkerPosition([lat, lng]);
    setLocalForm((prev) => ({ ...prev, latitude: lat, longitude: lng }));
  }, []);

  const uploadImages = async (images) => {
    const uploadPromises = images.map(async (file) => {
      const imageRef = ref(storage, `immo/${Date.now()}-${file.name}`);
      await uploadBytes(imageRef, file);
      return await getDownloadURL(imageRef);
    });
    return Promise.all(uploadPromises);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (localForm.priceType === "m2" && !localForm.area) {
      alert("Veuillez saisir la surface pour un prix au m²");
      return;
    }


    setUploading(true);
    try {
      let imagesUrls = localForm.imagesUrls || [];
      if (localForm.images.length > 0) {
        const newUrls = await uploadImages(localForm.images);
        imagesUrls = [...imagesUrls, ...newUrls];
      }

      const mainImageUrl =
        localForm.mainImageUrl || (imagesUrls.length > 0 ? imagesUrls[0] : "");

      const { images, source, promoteurAccepted, ...formDataWithoutFiles } = localForm;

      const propertyData = {
        ...formDataWithoutFiles,
        imagesUrls,
        mainImageUrl,
        price: localForm.price || null,
        latitude: markerPosition ? markerPosition[0] : null,
        longitude: markerPosition ? markerPosition[1] : null,
        location: localForm.location,
        livreFoncier: localForm.livreFoncier,
        acte: localForm.acte,
        vsp: localForm.vsp,
      };

      if (isEditing) {
        await onUpdate(propertyData);
      } else {
        await onAdd(propertyData);
      }

      setLocalForm(initialForm);
      setMarkerPosition(null);
    } catch (error) {
      console.error("❌ Erreur formulaire :", error);
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit} className={styles.formContainer}>
<h3>
  {isEditing ? editTitle : addTitle}
</h3>

        {/* Nom */}
        <input
          type="text" name="name"
          placeholder="Nom du bien"
          value={localForm.name}
          onChange={handleInputChange}
          className={styles.formInput}
        />

        {/* Prix */}
        <input
          type="text" name="price"
          placeholder="Prix (ex: 1 200 000)"
          value={localForm.price}
          onChange={handleInputChange}
          className={styles.formInput}
        />

        {/* Type de prix */}
        <div className={styles.categorySelector}>
          <label><strong>Type de prix :</strong></label>
          <select name="priceType" value={localForm.priceType}
            onChange={handleInputChange} className={styles.formInput}>
            <option value="total">Prix Total (DA)</option>
            <option value="m2">Prix par m² (DA / m²)</option>
          </select>
        </div>

        {/* Localisation */}
        <input
          type="text" name="location"
          placeholder="Localisation (ex: Millénium, Oran)"
          value={localForm.location}
          onChange={handleInputChange}
          className={styles.formInput}
        />

        {/* Surface */}
        <input
          type="text" name="area"
          placeholder="Surface (m²)"
          value={localForm.area}
          onChange={handleInputChange}
          className={styles.formInput}
        />

        {/* Pièces */}
        <input
          type="text"
          name="rooms"
          placeholder="Typologies (ex: F3, T4, F3-F5...)"
          value={localForm.rooms}
          onChange={handleInputChange}
          className={styles.formInput}
        />

        {/* Description */}
        <textarea
          name="description"
          placeholder="Description"
          value={localForm.description}
          onChange={handleInputChange}
          className={styles.formTextarea}
        />

        {/* Images */}
        <input
          type="file" multiple
          onChange={handleFileChange}
          className={styles.formInput}
        />
        <ImageUploader formData={localForm} setFormData={setLocalForm} />

        {/* 📒 Livret Foncier */}
        <div className={styles.categorySelector}>
          <label><strong>📒 Livret Foncier :</strong></label>
          <div className={styles.categoryButtons}>
            {[
              { value: true,  label: "✅ Prêt" },
              { value: false, label: "⏳ En cours" },
              { value: null,  label: "❓ Non renseigné" },
            ].map(({ value, label }) => (
              <button key={String(value)} type="button"
                className={`${styles.categoryBtn} ${localForm.livreFoncier === value ? styles.active : ""}`}
                onClick={() => setLocalForm(prev => ({ ...prev, livreFoncier: value }))}>
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* 📝 Acte */}
        <div className={styles.categorySelector}>
          <label><strong>📝 Acte :</strong></label>
          <div className={styles.categoryButtons}>
            {[
              { value: true,  label: "✅ Prêt" },
              { value: false, label: "⏳ En cours" },
              { value: null,  label: "❓ Non renseigné" },
            ].map(({ value, label }) => (
              <button key={String(value)} type="button"
                className={`${styles.categoryBtn} ${localForm.acte === value ? styles.active : ""}`}
                onClick={() => setLocalForm(prev => ({ ...prev, acte: value }))}>
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* 🏛️ VSP */}
        <div className={styles.categorySelector}>
          <label><strong>🏛️ VSP :</strong></label>
          <div className={styles.categoryButtons}>
            {[
              { value: true,  label: "✅ Avec VSP" },
              { value: false, label: "⚠️ Sans VSP" },
              { value: null,  label: "❓ Non renseigné" },
            ].map(({ value, label }) => (
              <button key={String(value)} type="button"
                className={`${styles.categoryBtn} ${localForm.vsp === value ? styles.active : ""}`}
                onClick={() => setLocalForm(prev => ({ ...prev, vsp: value }))}>
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Carte GPS */}
        <div style={{ marginTop: "16px" }}>
          <label style={{
            fontSize: "13px", fontWeight: "600",
            color: "#374151", marginBottom: "4px", display: "block",
          }}>
            🗺️ Position GPS{" "}
            <span style={{ color: "#6b7280", fontWeight: "400", fontSize: "12px" }}>
              (optionnel)
            </span>
          </label>
          <p style={{ fontSize: "12px", color: "#6b7280", marginBottom: "8px" }}>
            Cliquez sur la carte pour épingler la position exacte du bien.
          </p>

          {mapReady && (
            <MapContainer
              center={markerPosition || [36.7525, 3.042]}
              zoom={12}
              style={{ height: "320px", width: "100%", borderRadius: "10px", zIndex: 0 }}
            >
              <TileLayer
                attribution="&copy; OpenStreetMap contributors"
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <ClickHandler onMapClick={handleMapClick} />
              {markerPosition && (
                <>
                  <FlyToPosition position={markerPosition} />
                  <Marker position={markerPosition}>
                    <Popup>{localForm.location || "Position sélectionnée"}</Popup>
                  </Marker>
                </>
              )}
            </MapContainer>
          )}

          <div style={{
            marginTop: "10px", padding: "10px 14px",
            background: markerPosition ? "#f0fdf4" : "#f9fafb",
            border: `1px solid ${markerPosition ? "#bbf7d0" : "#e5e7eb"}`,
            borderRadius: "8px", fontSize: "13px",
            color: markerPosition ? "#065f46" : "#9ca3af",
            minHeight: "40px", display: "flex", alignItems: "center", gap: "8px",
          }}>
            {markerPosition ? (
              <>
                <span>✅</span>
                <span>Position GPS enregistrée :</span>
                <span style={{ fontWeight: "600" }}>
                  {markerPosition[0].toFixed(5)}, {markerPosition[1].toFixed(5)}
                </span>
                <button
                  type="button"
                  onClick={() => {
                    setMarkerPosition(null);
                    setLocalForm(prev => ({ ...prev, latitude: null, longitude: null }));
                  }}
                  style={{
                    marginLeft: "auto", background: "none", border: "none",
                    color: "#ef4444", cursor: "pointer", fontSize: "13px",
                  }}
                >
                  ✖ Supprimer
                </button>
              </>
            ) : (
              <span>Aucune position GPS sélectionnée</span>
            )}
          </div>
        </div>

        {/* Bouton submit */}
        <button
          type="submit"
          disabled={processing || uploading}
          className={styles.formButton}
          style={{ marginTop: "20px" }}
        >
          {processing || uploading
            ? "⏳ En cours..."
            : isEditing ? "💾 Mettre à jour" : "➕ Ajouter"}
        </button>
      </form>
    </>
  );
};

export default ImmoForm;